import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import BackNavbar from '../../../../ReusableBlocks/Navbar';
import InternalSEAnalyticsSection from './InternalSEAnalyticsSection';
import PartnerAnalyticsSection from './PartnerAnalyticsSection';
import BootcampStatsSection from './BootcampStatsSection';
import PeriodFilterDropdown from './PeriodFilterDropdown';
import LeadershipExportDrawer from './export/LeadershipExportDrawer';
import { INTERNAL_SE_EXPORT_CONFIG } from './export/internalSEExportConfig';
import {
  getPartnerExportConfig,
  getPartnerPeriodLabel,
} from './export/partnerAnalyticsExportConfig';
import { useLeadershipExport } from './export/useLeadershipExport';
import { canAccessLeadershipAnalytics } from './leadershipAnalyticsAccess';
import {
  IMPACT_CONSOLE_TABS,
  isImpactConsoleTab,
} from '../SalesImpactAnalytics/impactConsoleTabs';
import '../../../../Styles/LeadershipExportDrawer.css';

const TABS = [
  { id: 'internal', label: 'Internal SE Analytics' },
  { id: 'partner', label: 'Partner Analytics' },
  { id: 'bootcamp', label: 'Solution Demo Analytics' },
  ...IMPACT_CONSOLE_TABS.map(({ id, label }) => ({ id, label })),
];

const LeadershipConsole = () => {
  const currentUser = useSelector((state) => state.auth?.currentUser);
  const hasLeadershipAnalyticsAccess = canAccessLeadershipAnalytics(currentUser);
  const [activeTab, setActiveTab] = useState('internal');
  const [period, setPeriod] = useState('Week');
  const [, setInternalWarnings] = useState([]);
  const [internalSELoading, setInternalSELoading] = useState(true);
  const [partnerLoading, setPartnerLoading] = useState(true);
  const [selectedPartnerCompany, setSelectedPartnerCompany] = useState('All Partner Companies');
  const [partnerCompanyOptions, setPartnerCompanyOptions] = useState(['All Partner Companies']);
  const [containerClass, setContainerClass] = useState('container');

  const internalExport = useLeadershipExport({
    exportConfig: INTERNAL_SE_EXPORT_CONFIG,
    filterValues: { period },
    disabled: internalSELoading,
  });

  const partnerExportConfig = useMemo(
    () => getPartnerExportConfig(selectedPartnerCompany),
    [selectedPartnerCompany]
  );

  const partnerExport = useLeadershipExport({
    exportConfig: partnerExportConfig,
    filterValues: {
      period: getPartnerPeriodLabel(period),
      partnerCompany: selectedPartnerCompany,
    },
    disabled: partnerLoading,
  });

  useEffect(() => {
    const handleResize = () => {
      setContainerClass(window.innerWidth < 992 ? 'container-fluid' : 'container');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <BackNavbar />

      <div className="manager-console-background lead-dash-page">
        <section>
          <div className="leadership-console-hero-section">
            <div className="container">
              <div className="learning-hub-micro-certs-her-content">
                <h6 className="learning-hub-micro-certs-her-content-h1">
                  Leadership Console
                </h6>
              </div>
            </div>
          </div>
        </section>

        <div className={`${containerClass} leadership-analytics-container`}>
            {!hasLeadershipAnalyticsAccess ? (
              <div className="leadership-access-placeholder">
                <h2 className="leadership-access-placeholder-title">Leadership Analytics</h2>
                <p className="leadership-access-placeholder-message">
                  You do not have permission to view Leadership Analytics.
                </p>
              </div>
            ) : (
              <>
            <div className="lead-dash-tabs" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`lead-dash-tab ${activeTab === tab.id ? 'lead-dash-tab-active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab !== 'bootcamp' && !isImpactConsoleTab(activeTab) && (
              <div className="lead-dash-toolbar">
                <h2 className="lead-dash-section-label">Key Performance Indicators</h2>
                <div className="lead-dash-toolbar-actions">
                  {activeTab === 'partner' && (
                    <div className="lead-partner-top-control">
                      <label className="lead-partner-dropdown-label" htmlFor="lead-partner-company-select">
                        Partner Company:
                      </label>
                      <select
                        id="lead-partner-company-select"
                        className="lead-dash-select lead-period-inline-select partner-company-select"
                        value={selectedPartnerCompany}
                        onChange={(e) => setSelectedPartnerCompany(e.target.value)}
                      >
                        {partnerCompanyOptions.map((company) => (
                          <option key={company} value={company}>
                            {company}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <PeriodFilterDropdown value={period} onChange={setPeriod} />
                  {activeTab === 'internal' && (
                    <button
                      type="button"
                      className="leadership-export-trigger-btn"
                      onClick={() => internalExport.setIsExportDrawerOpen(true)}
                      disabled={internalSELoading || internalExport.isExporting}
                    >
                      Export
                    </button>
                  )}
                  {activeTab === 'partner' && (
                    <button
                      type="button"
                      className="leadership-export-trigger-btn"
                      onClick={() => partnerExport.setIsExportDrawerOpen(true)}
                      disabled={partnerLoading || partnerExport.isExporting}
                    >
                      Export
                    </button>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'internal' && (
              <InternalSEAnalyticsSection
                period={period}
                onWarningsChange={setInternalWarnings}
                onLoadingChange={setInternalSELoading}
              />
            )}

            {activeTab === 'partner' && (
              <PartnerAnalyticsSection
                period={period}
                isActive={activeTab === 'partner'}
                selectedPartnerCompany={selectedPartnerCompany}
                onSelectedPartnerCompanyChange={setSelectedPartnerCompany}
                onLoadingChange={setPartnerLoading}
                onPartnerCompanyOptionsChange={(nextOptions) => {
                  setPartnerCompanyOptions((prevOptions) => {
                    if (
                      prevOptions.length === nextOptions.length &&
                      prevOptions.every((item, idx) => item === nextOptions[idx])
                    ) {
                      return prevOptions;
                    }
                    return nextOptions;
                  });
                }}
              />
            )}

            {activeTab === 'bootcamp' && <BootcampStatsSection />}

            {IMPACT_CONSOLE_TABS.map(({ id, Component }) => (
              activeTab === id ? <Component key={id} /> : null
            ))}

            {activeTab === 'internal' && (
              <LeadershipExportDrawer
                isOpen={internalExport.isExportDrawerOpen}
                onClose={() => {
                  if (!internalExport.isExporting) {
                    internalExport.setIsExportDrawerOpen(false);
                  }
                }}
                exportConfig={INTERNAL_SE_EXPORT_CONFIG}
                selectedCount={internalExport.selectedCount}
                isSelected={internalExport.isSelected}
                onToggleItem={internalExport.toggleItem}
                onSelectAll={internalExport.selectAll}
                onClearAll={internalExport.clearAll}
                onExport={internalExport.handleExport}
                isExporting={internalExport.isExporting}
              />
            )}

            {activeTab === 'partner' && (
              <LeadershipExportDrawer
                isOpen={partnerExport.isExportDrawerOpen}
                onClose={() => {
                  if (!partnerExport.isExporting) {
                    partnerExport.setIsExportDrawerOpen(false);
                  }
                }}
                exportConfig={partnerExportConfig}
                selectedCount={partnerExport.selectedCount}
                isSelected={partnerExport.isSelected}
                onToggleItem={partnerExport.toggleItem}
                onSelectAll={partnerExport.selectAll}
                onClearAll={partnerExport.clearAll}
                onExport={partnerExport.handleExport}
                isExporting={partnerExport.isExporting}
              />
            )}
              </>
            )}
        </div>
      </div>
    </>
  );
};

export default LeadershipConsole;
